import { JsonSchema, UISchemaElement } from '@jsonforms/core';
import { materialCells } from '@jsonforms/material-renderers';
import { JsonForms } from '@jsonforms/react';
import { endpoints } from '@rossum/api-client';
import { HookInterface } from '@rossum/api-client/hookInterfaces';
import { SchemaAction } from '@rossum/api-client/schemas';
import { IconLayoutGrid } from '@rossum/ui/icons/tabler';
import {
  Alert,
  createSvgIcon,
  LinearProgress,
  MenuItem,
  TextField,
} from '@rossum/ui/material';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import * as z from 'zod';
import { useApiClient } from '../../../lib/apiClient';
import { renderers } from '../../extension-interop/custom-controls/renderers';
import { intentSchema } from '../../extension-interop/hooks/intent.schema';
import { createForm } from './createForm';

const events = [
  'annotation_imported',
  'annotation_confirmed',
  'annotation_exported',
  'validation',
] as const;

const customActionPayloadSchema = z.object({
  event: z.enum(events),
  hook_interface_id: z.number(),
  payload: z.unknown().default({}),
});

export const useInvokeHookInterfaceQuery = ({
  hookInterfaceId,
}: {
  hookInterfaceId: number;
}) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['invoke_hook_interface', hookInterfaceId],
    queryFn: async () => {
      const response = await api.request(
        endpoints.hookInterfaces.invoke(hookInterfaceId, {
          configure: true,
        })
      );
      return z
        .object({
          intent: intentSchema.optional(),
        })
        .parse(response);
    },
  });
};

export const CustomActionForm = createForm(({ value, onChange }) => {
  const intl = useIntl();

  // TODO: This does not work as intended, need someone to help me fix it :sadpanda:
  // const formTheme = useMemo(
  //   () =>
  //     createTheme({
  //       ...theme,
  //       components: {
  //         ...theme.components,
  //         MuiOutlinedInput: {
  //           ...(theme.components?.MuiOutlinedInput ?? {}),
  //           styleOverrides: {
  //             input: {
  //               // No idea why styling via default props does not work
  //               padding: '8.5px 14px',
  //             },
  //           },
  //         },
  //       },
  //     }),
  //   [theme]
  // );

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useInvokeHookInterfaceQuery({
    hookInterfaceId: value.hook_interface_id,
  });

  return isLoading ? (
    <LinearProgress />
  ) : isError ? (
    <Alert color="error">{error?.toString()}</Alert>
  ) : !response.intent ? (
    <Alert color="error">
      {intl.formatMessage({
        id: 'features.queueSettings.rules.actions.documentAction.custom.errorIntent',
      })}
    </Alert>
  ) : (
    <>
      <TextField
        select
        size="small"
        value={value.event}
        label={intl.formatMessage({
          id: 'features.queueSettings.rules.actions.documentAction.custom.run_on',
        })}
        onChange={e => {
          const newEvent = events.find(event => event === e.target.value);

          if (newEvent) {
            onChange({
              hook_interface_id: value.hook_interface_id,
              event: newEvent,
              payload: value.payload,
            });
          }
        }}
      >
        {events.map(event => (
          <MenuItem value={event} key={event}>
            {intl.formatMessage({
              id: `features.queueSettings.rules.actions.documentAction.custom.run_on.${event}`,
            })}
          </MenuItem>
        ))}
      </TextField>
      {response.intent.error ? (
        <Alert color="error">{response.intent.error.message}</Alert>
      ) : null}

      {response.intent.info ? (
        <Alert color="info">{response.intent.info.message}</Alert>
      ) : null}

      {response.intent.form ? (
        <JsonForms
          schema={response.intent?.form?.schema as JsonSchema}
          uischema={response.intent?.form?.uiSchema as UISchemaElement}
          data={value.payload}
          validationMode="NoValidation"
          renderers={renderers}
          cells={materialCells}
          onChange={({ data }) => {
            if (data !== value.payload) {
              onChange({
                hook_interface_id: value.hook_interface_id,
                event: value.event,
                payload: data,
              });
            }
          }}
        />
      ) : null}
    </>
  );
}, customActionPayloadSchema);

export const createCustomAction = (hook_interface: HookInterface) =>
  ({
    icon: createSvgIcon(<IconLayoutGrid />, 'IconLayoutGrid'),
    resolveAction: (a: SchemaAction) => {
      const parseResult = customActionPayloadSchema.safeParse(a.payload);
      return (
        a.type === 'custom' &&
        parseResult.success === true &&
        parseResult.data.hook_interface_id === hook_interface.id
      );
    },
    serialized: {
      type: 'custom' as const,
      payload: {
        hook_interface_id: hook_interface.id,
        event: 'validation',
        payload: {},
      },
    },
    groupIntlKey: 'other' as const,
    form: CustomActionForm,
    header: null,
  }) as const;
